<template>
	<header class="bg-surface-light border-b border-surface-grey sticky top-0 z-50">
		<DoorsaleBanner />
		<BaseContainer>
			<div class="flex justify-between h-20 items-center gap-4">
				<RouterLink
					to="/doorsale"
					class="flex-shrink-0 flex items-center w-25 h-8 lg:w-50 lg:h-12"
				>
					<GlobalCustomerLogo />
				</RouterLink>

				<nav class="hidden md:flex justify-center h-full w-full">
					<div class="space-x-2 flex">
						<BaseDropdown						v-if="availableSpecialCategories.length > 0"
						ref="specialCategoryDropdownRef"
						class="hidden md:inline-flex"
						@on-change="() => closeOtherDropdowns('special_category')"
					>
						<template #trigger>
							<span class="text-sm lg:text-base flex items-center justify-center cursor-pointer px-2 lg:px-4 py-4 h-full font-semibold">
								{{ $t('globals.special_categories') }}
								<DropdownIcon class="ml-2 h-3 w-3" />
							</span>
						</template>
						<div class="flex flex-wrap gap-2 py-4 md:py-0 w-full justify-center max-h-62 overflow-y-auto md:max-h-none">
							<BaseButton
								v-for="sc in availableSpecialCategories"
								:key="sc.id"
								color="muted"
								class="px-3 py-1 max-w-full min-w-0"
								:class="{ '!border-primary': selectedSpecialCategory === sc.name }"
								@click="handleSpecialCategoryClick(sc.name)"
							>
								<span class="block max-w-[400px] overflow-hidden text-ellipsis whitespace-nowrap">
									{{ sc.name }}
								</span>
							</BaseButton>
						</div>
					</BaseDropdown>

					<BaseDropdown							ref="categoryDropdownRef"
							class="hidden md:inline-flex"
							@on-change="() => closeOtherDropdowns('category')"
						>
							<template #trigger>
								<span class="text-sm lg:text-base flex items-center justify-center cursor-pointer px-2 lg:px-4 py-4 h-full font-semibold">
									{{ $t('globals.categories') }}
									<DropdownIcon class="ml-2 h-3 w-3" />
								</span>
							</template>
							<div class="flex flex-wrap gap-2 py-4 md:py-0 w-full justify-center max-h-62 overflow-y-auto md:max-h-none">
								<BaseButton
									v-for="cat in availableCategories"
									:key="cat.id"
									color="muted"
									class="px-3 py-1 max-w-full min-w-0"
									:class="{ '!border-primary': selectedCategory === cat.name }"
									@click="handleCategoryClick(cat.name)"
								>
									<span class="block max-w-[400px] overflow-hidden text-ellipsis whitespace-nowrap">
										{{ getLocalizedCategoryValue(cat, locale, 'name') }}
									</span>
								</BaseButton>
							</div>
						</BaseDropdown>

						<BaseDropdown
							ref="locationDropdownRef"
							class="hidden md:inline-flex"
							@on-change="() => closeOtherDropdowns('location')"
						>
							<template #trigger>
								<span class="text-sm lg:text-base flex items-center justify-center cursor-pointer px-2 lg:px-4 py-4 h-full font-semibold">
									{{ $t('globals.locations') }}
									<DropdownIcon class="ml-2 h-3 w-3" />
								</span>
							</template>
							<div class="flex flex-wrap gap-2 py-4 md:py-0 w-full justify-center max-h-62 overflow-y-auto md:max-h-none">
								<BaseButton
									v-for="venue in availableVenues"
									:key="venue.id"
									color="muted"
									class="px-3 py-1 max-w-full min-w-0"
									:class="{ '!border-primary': selectedLocation === venue.name }"
									@click="handleLocationClick(venue.name)"
								>
									<span class="block max-w-[400px] overflow-hidden text-ellipsis whitespace-nowrap">
										{{ venue.name }}
									</span>
								</BaseButton>
							</div>
						</BaseDropdown>

						<BaseDropdown
							ref="dateDropdownRef"
							class="hidden md:inline-flex"
							@on-change="() => closeOtherDropdowns('date')"
						>
							<template #trigger>
								<span class="text-sm lg:text-base flex items-center justify-center cursor-pointer px-2 lg:px-4 py-4 h-full font-semibold">
									{{ $t('globals.date') }}
									<DropdownIcon class="ml-2 h-3 w-3" />
								</span>
							</template>
							<div class="flex flex-wrap py-4 md:py-0 gap-2 w-full justify-center">
								<BaseDatePicker
									v-model="localDate"
									:available-dates="availableDates"
									:min-date="minDate"
									:max-date="maxDate"
								/>
							</div>
						</BaseDropdown>
					</div>
				</nav>

				<div class="flex items-center gap-2 flex-shrink-0">
					<BaseDropdown
						ref="searchDropdownRef"
						class="hidden md:inline-flex"
						@on-change="handleSearchDropdownChange"
					>
						<template #trigger>
							<BaseButton is="span" color="accent">
								<ZoomIcon class="h-6 w-6 fill-current mr-2" />
								<span class="hidden sm:inline">{{ $t('globals.search') }}</span>
							</BaseButton>
						</template>
						<div class="flex md:justify-center">
							<input
								ref="searchInputRef"
								type="text"
								:value="searchText"
								@input="handleSearchInput"
								:placeholder="$t('doorsale.search_placeholder')"
								class="w-full max-w-xl md:text-lg md:px-6 py-2 md:py-4 focus:outline-none"
								@keydown.enter="searchDropdownRef?.closeDropdown()"
							/>
						</div>
					</BaseDropdown>

					<!-- Mobile menu -->
					<BaseDropdown
						ref="mobileMenuDropdownRef"
						class="md:hidden"
						background-class="bg-surface-light"
						@on-change="handleMobileDropdownChange"
					>
						<template #trigger>
							<span
								:class="[
									'rounded-full items-center justify-center w-12 h-12 flex transition-colors border border-accent',
									mobileMenuOpen ? 'bg-white' : 'bg-accent',
								]"
							>
								<ZoomFilterIcon v-if="!mobileMenuOpen" class="h-6 w-6 fill-accent-text" />
								<CloseIcon v-else class="h-6 w-6 fill-accent" />
								<span class="sr-only">{{ $t('globals.search') }}</span>
							</span>
						</template>

						<div class="-mx-4 pt-2 max-h-[calc(100dvh-120px)] overflow-y-auto">
							<BaseAccordion							v-if="availableSpecialCategories.length > 0"
							:open="mobileOpenAccordion === 'special_categories'"
							@toggle="() => setMobileAccordion('special_categories')"
						>
							<template #trigger>
								<span :class="mobileOpenAccordion && mobileOpenAccordion !== 'special_categories' ? 'text-text' : ''">
									{{ $t('globals.special_categories') }}
								</span>
							</template>
							<div class="flex flex-wrap gap-2 py-2">
								<BaseButton
									v-for="sc in availableSpecialCategories"
									:key="sc.id"
									color="muted"
									class="px-3 py-1 max-w-full min-w-0"
									:class="{ '!border-primary': selectedSpecialCategory === sc.name }"
									@click="handleMobileSpecialCategoryClick(sc.name)"
								>
									<span class="block max-w-[280px] overflow-hidden text-ellipsis whitespace-nowrap">
										{{ sc.name }}
									</span>
								</BaseButton>
							</div>
						</BaseAccordion>

						<BaseAccordion								:open="mobileOpenAccordion === 'categories'"
								@toggle="() => setMobileAccordion('categories')"
							>
								<template #trigger>
									<span :class="mobileOpenAccordion && mobileOpenAccordion !== 'categories' ? 'text-text' : ''">
										{{ $t('globals.categories') }}
									</span>
								</template>
								<div class="flex flex-wrap gap-2 py-2">
									<BaseButton
										v-for="cat in availableCategories"
										:key="cat.id"
										color="muted"
										class="px-3 py-1 max-w-full min-w-0"
										:class="{ '!border-primary': selectedCategory === cat.name }"
										@click="handleMobileCategoryClick(cat.name)"
									>
										<span class="block max-w-[280px] overflow-hidden text-ellipsis whitespace-nowrap">
											{{ getLocalizedCategoryValue(cat, locale, 'name') }}
										</span>
									</BaseButton>
								</div>
							</BaseAccordion>

							<BaseAccordion
								:open="mobileOpenAccordion === 'locations'"
								@toggle="() => setMobileAccordion('locations')"
							>
								<template #trigger>
									<span :class="mobileOpenAccordion && mobileOpenAccordion !== 'locations' ? 'text-text' : ''">
										{{ $t('globals.locations') }}
									</span>
								</template>
								<div class="flex flex-wrap gap-2 py-2">
									<BaseButton
										v-for="venue in availableVenues"
										:key="venue.id"
										color="muted"
										class="px-3 py-1 max-w-full min-w-0"
										:class="{ '!border-primary': selectedLocation === venue.name }"
										@click="handleMobileLocationClick(venue.name)"
									>
										<span class="block max-w-[280px] overflow-hidden text-ellipsis whitespace-nowrap">
											{{ venue.name }}
										</span>
									</BaseButton>
								</div>
							</BaseAccordion>

							<BaseAccordion
								:open="mobileOpenAccordion === 'date'"
								@toggle="() => setMobileAccordion('date')"
							>
								<template #trigger>
									<span :class="mobileOpenAccordion && mobileOpenAccordion !== 'date' ? 'text-text' : ''">
										{{ $t('globals.date') }}
									</span>
								</template>
								<div class="py-2">
									<BaseDatePicker
										v-model="localDate"
										:available-dates="availableDates"
										:min-date="minDate"
										:max-date="maxDate"
									/>
								</div>
							</BaseAccordion>

							<BaseAccordion
								:open="mobileOpenAccordion === 'search'"
								@toggle="() => setMobileAccordion('search')"
							>
								<template #trigger>
									<span :class="mobileOpenAccordion && mobileOpenAccordion !== 'search' ? 'text-text' : ''">
										{{ $t('globals.search') }}
									</span>
								</template>
								<div class="py-2">
									<input
										ref="mobileSearchInputRef"
										type="text"
										:value="searchText"
										@input="handleSearchInput"
										:placeholder="$t('doorsale.search_placeholder')"
										class="w-full text-base px-2 py-2 focus:outline-none border-b border-surface-grey"
										@keydown.enter="closeMobileDropdownAndAccordion()"
									/>
								</div>
							</BaseAccordion>
						</div>
					</BaseDropdown>
				</div>
			</div>
		</BaseContainer>
	</header>
</template>

<script setup lang="ts">
	import { computed, ref, watch } from 'vue';
	import { useI18n } from 'vue-i18n';

	import GlobalCustomerLogo from '@/components/globals/GlobalCustomerLogo.vue';
	import CloseIcon from '@/components/icons/CloseIcon.vue';
	import DropdownIcon from '@/components/icons/DropdownIcon.vue';
	import ZoomFilterIcon from '@/components/icons/ZoomFilterIcon.vue';
	import ZoomIcon from '@/components/icons/ZoomIcon.vue';
	import BaseAccordion from '@/components/ui/BaseAccordion.vue';
	import BaseButton from '@/components/ui/BaseButton.vue';
	import BaseContainer from '@/components/ui/BaseContainer.vue';
	import BaseDatePicker from '@/components/ui/BaseDatePicker.vue';
	import BaseDropdown from '@/components/ui/BaseDropdown.vue';
	import { useGeneralStore } from '@/stores/general';
	import type { DoorsaleEvent } from '@/types';
	import { getEventtime } from '@/utils/eventItemUtil';
	import { getLocalizedCategoryValue } from '@/utils/getCategoryLocalization';

	import DoorsaleBanner from './DoorsaleBanner.vue';

	const { locale, t: $t } = useI18n();

	const props = defineProps<{
		events: DoorsaleEvent[];
		searchText: string;
		selectedSpecialCategory: string | null;
		selectedCategory: string | null;
		selectedLocation: string | null;
		selectedDate: number | null;
	}>();

	const emit = defineEmits<{
		'update:searchText': [value: string];
		'update:selectedSpecialCategory': [value: string | null];
		'update:selectedCategory': [value: string | null];
		'update:selectedLocation': [value: string | null];
		'update:selectedDate': [value: number | null];
	}>();

	const generalStore = useGeneralStore();

	const specialCategoryDropdownRef = ref<InstanceType<typeof BaseDropdown> | null>(null);
	const categoryDropdownRef = ref<InstanceType<typeof BaseDropdown> | null>(null);
	const locationDropdownRef = ref<InstanceType<typeof BaseDropdown> | null>(null);
	const dateDropdownRef = ref<InstanceType<typeof BaseDropdown> | null>(null);
	const searchDropdownRef = ref<InstanceType<typeof BaseDropdown> | null>(null);
	const searchInputRef = ref<HTMLInputElement | null>(null);
	const mobileMenuDropdownRef = ref<InstanceType<typeof BaseDropdown> | null>(null);
	const mobileSearchInputRef = ref<HTMLInputElement | null>(null);
	const mobileMenuOpen = ref(false);
	const mobileOpenAccordion = ref<string | null>(null);

	function handleMobileDropdownChange(val: boolean) {
		mobileMenuOpen.value = val;
		if (!val) mobileOpenAccordion.value = null;
	}

	function setMobileAccordion(type: string) {
		mobileOpenAccordion.value = mobileOpenAccordion.value === type ? null : type;
		if (mobileOpenAccordion.value === 'search') {
			setTimeout(() => mobileSearchInputRef.value?.focus(), 250);
		}
	}

	function closeMobileDropdownAndAccordion() {
		mobileOpenAccordion.value = null;
		mobileMenuDropdownRef.value?.closeDropdown();
		mobileMenuOpen.value = false;
	}

	function handleMobileSpecialCategoryClick(name: string) {
		handleSpecialCategoryClick(name);
		closeMobileDropdownAndAccordion();
	}

	function handleMobileCategoryClick(name: string) {
		handleCategoryClick(name);
		closeMobileDropdownAndAccordion();
	}

	function handleMobileLocationClick(name: string) {
		handleLocationClick(name);
		closeMobileDropdownAndAccordion();
	}

	function handleSearchDropdownChange(open: boolean) {
		closeOtherDropdowns('search');
		if (open) {
			setTimeout(() => searchInputRef.value?.focus(), 250);
		}
	}

	function closeOtherDropdowns(except: string) {
		if (except !== 'special_category' && specialCategoryDropdownRef.value) {
			specialCategoryDropdownRef.value.closeDropdown();
		}
		if (except !== 'category' && categoryDropdownRef.value) {
			categoryDropdownRef.value.closeDropdown();
		}
		if (except !== 'location' && locationDropdownRef.value) {
			locationDropdownRef.value.closeDropdown();
		}
		if (except !== 'date' && dateDropdownRef.value) {
			dateDropdownRef.value.closeDropdown();
		}
		if (except !== 'search' && searchDropdownRef.value) {
			searchDropdownRef.value.closeDropdown();
		}
	}

	// Derive available special categories from the fetched doorsale events
	const availableSpecialCategories = computed(() => {
		const usedIds = new Set(props.events.flatMap(e => e.special_categories ?? []));
		return generalStore.special_categories()
			.filter(sc => usedIds.has(sc.id))
			.sort((a, b) => a.name.localeCompare(b.name));
	});

	// Derive available categories from the fetched doorsale events
	const availableCategories = computed(() => {
		const usedCategoryIds = new Set(props.events.flatMap(e => e.categories ?? []));
		return generalStore.categories()
			.filter(cat => usedCategoryIds.has(cat.id) && !cat.hide_from_list)
			.sort((a, b) => a.name.localeCompare(b.name));
	});

	// Derive available venues from the fetched doorsale events
	const availableVenues = computed(() => {
		const usedVenueIds = new Set(props.events.map(e => e.venue).filter(Boolean));
		const uniqueVenues = Array.from(
			new Map(
				generalStore.venues()
					.filter(v => usedVenueIds.has(v.id))
					.map(v => [v.name, v])
			).values()
		);
		return uniqueVenues.sort((a, b) => a.name.localeCompare(b.name));
	});

	// Derive available dates from the fetched doorsale events
	const availableDates = computed(() =>
		props.events.map(e => getEventtime(e))
	);

	const minDate = computed(() => {
		if (!availableDates.value.length) return new Date();
		return new Date(Math.min(...availableDates.value) * 1000);
	});

	const maxDate = computed(() => {
		if (!availableDates.value.length) return new Date();
		return new Date(Math.max(...availableDates.value) * 1000);
	});

	// Bridge between selectedDate prop (number | null) and BaseDatePicker (number | undefined)
	const localDate = computed<number | undefined>({
		get: () => props.selectedDate ?? undefined,
		set: (val) => emit('update:selectedDate', val ?? null),
	});

	function handleSpecialCategoryClick(name: string) {
		const next = props.selectedSpecialCategory === name ? null : name;
		emit('update:selectedSpecialCategory', next);
		if (next) {
			emit('update:selectedCategory', null);
			emit('update:selectedLocation', null);
			emit('update:selectedDate', null);
			emit('update:searchText', '');
		}
		specialCategoryDropdownRef.value?.closeDropdown();
	}

	function handleCategoryClick(name: string) {
		const next = props.selectedCategory === name ? null : name;
		emit('update:selectedCategory', next);
		if (next) {
			emit('update:selectedSpecialCategory', null);
			emit('update:selectedLocation', null);
			emit('update:selectedDate', null);
			emit('update:searchText', '');
		}
		categoryDropdownRef.value?.closeDropdown();
	}

	function handleLocationClick(name: string) {
		const next = props.selectedLocation === name ? null : name;
		emit('update:selectedLocation', next);
		if (next) {
			emit('update:selectedSpecialCategory', null);
			emit('update:selectedCategory', null);
			emit('update:selectedDate', null);
			emit('update:searchText', '');
		}
		locationDropdownRef.value?.closeDropdown();
	}

	function handleSearchInput(event: Event) {
		const value = (event.target as HTMLInputElement).value;
		emit('update:searchText', value);
		if (value) {
			emit('update:selectedSpecialCategory', null);
			emit('update:selectedCategory', null);
			emit('update:selectedLocation', null);
			emit('update:selectedDate', null);
		}
	}

	// Close date dropdown after selection
	watch(localDate, (val) => {
		dateDropdownRef.value?.closeDropdown();
		if (val) {
			emit('update:selectedSpecialCategory', null);
			emit('update:selectedCategory', null);
			emit('update:selectedLocation', null);
			emit('update:searchText', '');
		}
	});
</script>
